import { computeFederalTax } from '../constants/federal-tax-brackets';

/**
 * Shared recalculation for the plan Tax Summary block (Total Tax / Taxable Income /
 * Effective Rate). Both surfaces that expose those fields for inline edit use this:
 * the plan Tax Summary card (client-plan-info) and RAP step 2 (dashboard-upload-create-plan).
 *
 * Total Tax cannot be rederived from Taxable Income alone. The as-filed figure
 * (1040 line 24, OCR'd into us_1040_total_tax) carries SE tax, NIIT, AMT,
 * preferential cap-gain rates and credits, none of which are recoverable from a
 * single taxable-income number. So an edit to Taxable Income moves Total Tax by the
 * marginal-rate delta and keeps the as-filed figure as the anchor, which is also how
 * every strategy savings line already computes (change x rate). A straight bracket
 * computation is used only when there is no anchor to move.
 */

/**
 * Plans store the short filing-status codes ('MFJ', 'MFS', 'HOH'); the bracket
 * tables in federal-tax-brackets.ts are keyed by the long form. Without this map a
 * bracket lookup silently returns 0.
 */
export function toBracketFilingStatus(filingStatus: string): string {
  switch (filingStatus) {
    case 'MFJ':
    case 'Married Filing Jointly':
      return 'Married Filing Jointly (MFJ)';
    case 'MFS':
    case 'Married Filing Separately':
      return 'Married Filing Separately (MFS)';
    case 'HOH':
      return 'Head of Household';
    case 'QSS':
      // Qualifying surviving spouse files on the MFJ brackets. normalizeFilingStatus()
      // emits 'QSS' from OCR, and the bracket tables have no such key, so without
      // this a QSS plan falls back to a tax of 0.
      return 'Married Filing Jointly (MFJ)';
    default:
      // 'Single', 'Head of Household', 'Estate', 'Trust' and the long MFJ/MFS
      // forms are already valid bracket keys.
      return filingStatus;
  }
}

/**
 * Whether an effective rate is meaningful at all. Canonical rule (868k4yvkt): a
 * taxable income of zero or less has no rate to report, so surfaces render
 * 'n/m (business loss)' instead of a percentage.
 */
export function isEffectiveRateMeaningful(taxableIncome: any): boolean {
  const taxable = Number(taxableIncome);
  return !isNaN(taxable) && taxable > 0;
}

/**
 * Effective rate as a percentage, to two decimals.
 * Canonical rule (868k4yvkt): Max(0, tax / taxable income), clamped to 0%-100%.
 * A negative total tax (refundable credits exceeding liability) reads as 0%.
 */
export function calculateEffectiveRate(totalTax: any, taxableIncome: any): number {
  const tax = Number(totalTax);
  const taxable = Number(taxableIncome);
  if (isNaN(tax) || isNaN(taxable) || taxable <= 0) {
    return 0;
  }
  const rate = (tax / taxable) * 100;
  if (isNaN(rate)) {
    return 0;
  }
  return Math.round(Math.min(Math.max(rate, 0), 100) * 100) / 100;
}

/**
 * Whether the Effective Rate should be recomputed from the current pair.
 *
 * A Total Tax of exactly 0 is a real outcome (credits fully offset the liability,
 * or a user entered 0), and it must produce 0% rather than leaving the previous
 * rate on screen. Only genuinely absent data is skipped, so a hand-set Effective
 * Rate on a plan whose Total Tax was never captured is not wiped to 0.
 *
 * Shared so the two surfaces cannot drift apart on this guard.
 */
export function shouldRecalculateEffectiveRate(totalTax: any, taxableIncome: any): boolean {
  if (totalTax === null || totalTax === undefined || totalTax === '') {
    return false;
  }
  return !isNaN(Number(totalTax)) && isEffectiveRateMeaningful(taxableIncome);
}

export interface TotalTaxRecalcInput {
  previousTaxableIncome: any;
  newTaxableIncome: any;
  /** Total Tax before the edit -- the anchor the delta is applied to. */
  currentTotalTax: any;
  /** Marginal rate as a percentage, already recalculated for newTaxableIncome. */
  marginalRate: any;
  filingStatus?: string;
  /** True once a user has typed Total Tax by hand. Suppresses the bracket fallback. */
  totalTaxOverridden?: boolean;
}

/**
 * Total Tax after a Taxable Income edit.
 *
 * The delta path is the normal one: anchor + (change in taxable income x marginal
 * rate). The bracket computation is a fallback for when there is no anchor to move,
 * and it is suppressed once a user has typed Total Tax by hand.
 *
 * A user-entered 0 is a real anchor, not a missing one. It suppresses the fallback
 * AND still moves by the delta on later edits, because the override records
 * provenance; it is not a freeze.
 */
export function recalculateTotalTax(input: TotalTaxRecalcInput): number {
  const newTaxable = Number(input.newTaxableIncome);
  const previousTaxable = Number(input.previousTaxableIncome);
  const anchor = Number(input.currentTotalTax);
  const safeAnchor = isNaN(anchor) ? 0 : anchor;

  if (isNaN(newTaxable)) {
    return safeAnchor;
  }

  // No anchor to move, and no human figure to protect: estimate from the brackets.
  if (safeAnchor <= 0 && !input.totalTaxOverridden) {
    return computeFederalTax(Math.max(0, newTaxable), toBracketFilingStatus(input.filingStatus));
  }

  const rate = Number(input.marginalRate);
  if (isNaN(previousTaxable) || isNaN(rate)) {
    return safeAnchor;
  }

  const delta = (newTaxable - previousTaxable) * (rate / 100);
  return Math.max(0, Math.round((safeAnchor + delta) * 100) / 100);
}
